import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  FileText,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Link as LinkIcon,
  Copy,
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  MessageSquare,
  Send,
  Mail,
  Phone,
  FileCheck,
  ArrowLeft,
  Euro,
  BarChart3,
  ClipboardList,
  CalendarDays,
  Lock,
  Unlock,
  MapPin,
  UserCheck,
  XCircle,
  Award,
  Key
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_TERRACOTTA = "https://customer-assets.emergentagent.com/job_academy-session/artifacts/gtms0r7v_Logo%20Agence%20Immobilier%20Minimaliste%20Marron%20Gris%20Beige%20-%203.PNG";

// Note type icons mapping
const NOTE_ICONS = {
  note: MessageSquare,
  email_sent: Send,
  email_received: Mail,
  call: Phone,
  payment: Euro,
  questionnaire: ClipboardList,
  qcm: ClipboardList,
  certificate: FileCheck,
  system: Settings
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [participants, setParticipants] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [session, setSession] = useState(null);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Selected participant for detail view
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [showParticipantDetail, setShowParticipantDetail] = useState(false);
  
  // Selected session for detail view
  const [selectedSessionDetail, setSelectedSessionDetail] = useState(null);
  const [showSessionDetail, setShowSessionDetail] = useState(false);
  
  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [allSessionsForCalendar, setAllSessionsForCalendar] = useState([]);
  
  // Settings state
  const [adminSettings, setAdminSettings] = useState(null);
  const [revolutKeyInput, setRevolutKeyInput] = useState('');
  const [revolutMode, setRevolutMode] = useState('sandbox');
  const [savingSettings, setSavingSettings] = useState(false);
  
  // QCM detail modal
  const [showQcmDetailModal, setShowQcmDetailModal] = useState(false);
  const [qcmDetailData, setQcmDetailData] = useState(null);
  const [loadingQcmDetail, setLoadingQcmDetail] = useState(false);
  
  // Questionnaire answers modal
  const [showQuestionnaireAnswersModal, setShowQuestionnaireAnswersModal] = useState(false);
  const [questionnaireAnswersData, setQuestionnaireAnswersData] = useState(null);
  
  // QCM admin management
  const [adminQcms, setAdminQcms] = useState([]);
  const [editingQcm, setEditingQcm] = useState(null);
  const [editingQuestionIdx, setEditingQuestionIdx] = useState(null);
  const [qcmStats, setQcmStats] = useState(null);
  const [showQcmStats, setShowQcmStats] = useState(false);
  const [qcmEditForm, setQcmEditForm] = useState({ text: '', options: ['', '', '', ''], correct: 'A' });
  
  // Modals
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  
  // Edit states
  const [sessionForm, setSessionForm] = useState({});
  const [questionnaireForm, setQuestionnaireForm] = useState({ title: '', type: 'milieu', questions: [] });
  const [paymentForm, setPaymentForm] = useState({ amount: 150, reference: '' });
  const [noteForm, setNoteForm] = useState({ content: '', type: 'note' });
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });

  // Email management (templates, manual send, logs)
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [templateDrafts, setTemplateDrafts] = useState({});
  const [savingTemplate, setSavingTemplate] = useState(null);
  const [manualEmail, setManualEmail] = useState({ target_type: 'participant', participant_id: '', session_id: '', subject: '', body: '' });
  const [manualTemplateKey, setManualTemplateKey] = useState('');
  const [sendingManual, setSendingManual] = useState(false);
  
  const navigate = useNavigate();

  // Training session management states
  const [showCreateSessionModal, setShowCreateSessionModal] = useState(false);
  const [showIncludePast, setShowIncludePast] = useState(false);
  const [newSessionForm, setNewSessionForm] = useState({
    date_jour1: '', date_jour2: '', heure_debut: '09:00', heure_fin: '17:00',
    places_total: 8, tarif: 150, lieu: 'Zoom', description: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAllData();
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.get(`${API}/training-sessions?include_past=${showIncludePast}`).then(res => {
        setTrainingSessions(res.data);
      });
    }
  }, [showIncludePast]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [participantsRes, questionnairesRes, answersRes, sessionRes, statsRes, trainingRes, allTrainingRes] = await Promise.all([
        axios.get(`${API}/participants`),
        axios.get(`${API}/questionnaires`),
        axios.get(`${API}/answers`),
        axios.get(`${API}/session`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/training-sessions?include_past=${showIncludePast}`),
        axios.get(`${API}/training-sessions?include_past=true`)
      ]);
      setParticipants(participantsRes.data);
      setQuestionnaires(questionnairesRes.data);
      setAnswers(answersRes.data);
      setSession(sessionRes.data);
      setSessionForm(sessionRes.data);
      setDashboardStats(statsRes.data);
      setTrainingSessions(trainingRes.data);
      setAllSessionsForCalendar(allTrainingRes.data);
      fetchAdminSettings();
      fetchAdminQcms();
      fetchEmailData();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParticipantDetail = async (participantId) => {
    try {
      const res = await axios.get(`${API}/participants/${participantId}`);
      setParticipantDetail(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du participant');
    }
  };

  const fetchSessionDetail = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/training-sessions/${sessionId}`);
      setSelectedSessionDetail(res.data);
      setShowSessionDetail(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de la session');
    }
  };

  const fetchAdminSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setAdminSettings(res.data);
      setRevolutMode(res.data.revolut_mode || 'sandbox');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveRevolutSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = { revolut_mode: revolutMode };
      if (revolutKeyInput) payload.revolut_api_key = revolutKeyInput;
      const res = await axios.post(`${API}/admin/settings`, payload);
      setAdminSettings(res.data);
      setRevolutKeyInput('');
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchQcmDetails = async (participantId, participantName) => {
    setLoadingQcmDetail(true);
    try {
      const [qcmRes, questRes] = await Promise.all([
        axios.get(`${API}/admin/participant/${participantId}/qcm-details`),
        axios.get(`${API}/admin/participant/${participantId}/questionnaire-answers`)
      ]);
      setQcmDetailData({ participantName, qcms: qcmRes.data, questionnaires: questRes.data });
      setShowQcmDetailModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoadingQcmDetail(false);
    }
  };

  const fetchAdminQcms = async () => {
    try {
      const res = await axios.get(`${API}/admin/qcms`);
      setAdminQcms(res.data);
    } catch (error) {
      console.error('Error fetching admin QCMs:', error);
    }
  };

  const fetchQcmStats = async (qcmId) => {
    try {
      const res = await axios.get(`${API}/admin/qcms/${qcmId}/stats`);
      setQcmStats(res.data);
      setShowQcmStats(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const openEditQuestion = (qcmId, qIndex, question) => {
    setEditingQcm(qcmId);
    setEditingQuestionIdx(qIndex);
    setQcmEditForm({
      text: question.text,
      options: [...question.options],
      correct: question.correct
    });
  };

  const saveQuestion = async () => {
    try {
      await axios.put(`${API}/admin/qcms/${editingQcm}/questions/${editingQuestionIdx}`, qcmEditForm);
      toast.success('Question sauvegardée');
      setEditingQuestionIdx(null);
      fetchAdminQcms();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const addNewQuestion = async (qcmId) => {
    try {
      await axios.post(`${API}/admin/qcms/${qcmId}/questions`, {
        text: 'Nouvelle question',
        options: ['Option A', 'Option B', 'Option C', 'Option D'],
        correct: 'A'
      });
      toast.success('Question ajoutée');
      fetchAdminQcms();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const deleteQuestion = async (qcmId, qIndex) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    try {
      await axios.delete(`${API}/admin/qcms/${qcmId}/questions/${qIndex}`);
      toast.success('Question supprimée');
      fetchAdminQcms();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const saveQcmTitle = async (qcmId, newTitle) => {
    try {
      await axios.put(`${API}/admin/qcms/${qcmId}`, { title: newTitle });
      toast.success('Titre mis à jour');
      fetchAdminQcms();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleDeleteParticipant = async (id) => {
    if (!window.confirm('Supprimer ce participant ?')) return;
    try {
      await axios.delete(`${API}/participants/${id}`);
      toast.success('Participant supprimé');
      fetchAllData();
      if (showParticipantDetail) {
        setShowParticipantDetail(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateSession = async () => {
    try {
      await axios.put(`${API}/session`, sessionForm);
      toast.success('Session mise à jour');
      setShowSessionModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveQuestionnaire = async () => {
    try {
      if (selectedQuestionnaire) {
        await axios.put(`${API}/questionnaires/${selectedQuestionnaire.id}`, questionnaireForm);
        toast.success('Questionnaire mis à jour');
      } else {
        await axios.post(`${API}/questionnaires`, questionnaireForm);
        toast.success('Questionnaire créé');
      }
      setShowQuestionnaireModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteQuestionnaire = async (id) => {
    if (!window.confirm('Supprimer ce questionnaire ?')) return;
    try {
      await axios.delete(`${API}/questionnaires/${id}`);
      toast.success('Questionnaire supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMarkPayment = async () => {
    if (!selectedParticipant) return;
    try {
      await axios.post(
        `${API}/participants/${selectedParticipant.id}/payment?amount=${paymentForm.amount}&reference=${paymentForm.reference}`
      );
      toast.success('Paiement enregistré');
      setShowPaymentModal(false);
      fetchAllData();
      fetchParticipantDetail(selectedParticipant.id);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleAddNote = async () => {
    if (!selectedParticipant || !noteForm.content.trim()) return;
    try {
      await axios.post(`${API}/participants/${selectedParticipant.id}/notes`, noteForm);
      toast.success('Note ajoutée');
      setShowNoteModal(false);
      setNoteForm({ content: '', type: 'note' });
      fetchParticipantDetail(selectedParticipant.id);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleSendEmail = async () => {
    if (!selectedParticipant || !emailForm.subject || !emailForm.body) return;
    try {
      await axios.post(`${API}/send-email?participant_id=${selectedParticipant.id}`, {
        to: selectedParticipant.email,
        subject: emailForm.subject,
        body: emailForm.body
      });
      toast.success('Email envoyé');
      setShowEmailModal(false);
      setEmailForm({ subject: '', body: '' });
      fetchParticipantDetail(selectedParticipant.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const fetchEmailData = async () => {
    try {
      const [tplRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/email-templates`),
        axios.get(`${API}/admin/email-logs`)
      ]);
      setEmailTemplates(tplRes.data);
      const drafts = {};
      tplRes.data.forEach(t => { drafts[t.key] = { subject: t.subject, body: t.body, enabled: t.enabled }; });
      setTemplateDrafts(drafts);
      setEmailLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching email data:', error);
    }
  };

  const updateDraft = (key, field, value) => {
    setTemplateDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const saveTemplate = async (key) => {
    setSavingTemplate(key);
    try {
      const d = templateDrafts[key];
      await axios.put(`${API}/admin/email-templates/${key}`, { subject: d.subject, body: d.body });
      toast.success('Modèle enregistré');
      fetchEmailData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingTemplate(null);
    }
  };

  const toggleTemplate = async (key) => {
    const newEnabled = !templateDrafts[key]?.enabled;
    updateDraft(key, 'enabled', newEnabled);
    try {
      await axios.put(`${API}/admin/email-templates/${key}`, { enabled: newEnabled });
      setEmailTemplates(prev => prev.map(t => t.key === key ? { ...t, enabled: newEnabled } : t));
      toast.success(newEnabled ? 'Email automatique activé' : 'Email automatique désactivé');
    } catch (error) {
      updateDraft(key, 'enabled', !newEnabled);
      toast.error('Erreur');
    }
  };

  const loadTemplateIntoManual = (key) => {
    setManualTemplateKey(key);
    const t = emailTemplates.find(x => x.key === key);
    if (t) setManualEmail(prev => ({ ...prev, subject: t.subject, body: t.body }));
  };

  const sendManualEmail = async () => {
    if (!manualEmail.subject || !manualEmail.body) {
      toast.error('Objet et message obligatoires');
      return;
    }
    if (manualEmail.target_type === 'participant' && !manualEmail.participant_id) {
      toast.error('Choisissez un participant');
      return;
    }
    if (manualEmail.target_type === 'session' && !manualEmail.session_id) {
      toast.error('Choisissez une session');
      return;
    }
    setSendingManual(true);
    try {
      const payload = { target_type: manualEmail.target_type, subject: manualEmail.subject, body: manualEmail.body };
      if (manualEmail.target_type === 'participant') payload.participant_id = manualEmail.participant_id;
      if (manualEmail.target_type === 'session') payload.session_id = manualEmail.session_id;
      const res = await axios.post(`${API}/admin/send-manual-email`, payload);
      toast.success(`${res.data.sent} email(s) envoyé(s)${res.data.failed ? ` · ${res.data.failed} échec(s)` : ''}`);
      setManualEmail({ target_type: manualEmail.target_type, participant_id: '', session_id: '', subject: '', body: '' });
      setManualTemplateKey('');
      fetchEmailData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingManual(false);
    }
  };

  const openQuestionnaireEdit = (questionnaire = null) => {
    if (questionnaire) {
      setSelectedQuestionnaire(questionnaire);
      setQuestionnaireForm({
        title: questionnaire.title,
        type: questionnaire.type,
        questions: questionnaire.questions.map(q => ({
          text: q.text,
          type: q.type,
          options: q.options || []
        }))
      });
    } else {
      setSelectedQuestionnaire(null);
      setQuestionnaireForm({ title: '', type: 'milieu', questions: [] });
    }
    setShowQuestionnaireModal(true);
  };

  const addQuestion = () => {
    setQuestionnaireForm({
      ...questionnaireForm,
      questions: [...questionnaireForm.questions, { text: '', type: 'text', options: [] }]
    });
  };

  const updateQuestion = (index, field, value) => {
    setQuestionnaireForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === index ? { ...q, [field]: value } : q)
    }));
  };

  const removeQuestion = (index) => {
    setQuestionnaireForm({
      ...questionnaireForm,
      questions: questionnaireForm.questions.filter((_, i) => i !== index)
    });
  };

  const openParticipantDetail = async (participant) => {
    setSelectedParticipant(participant);
    await fetchParticipantDetail(participant.id);
    setShowParticipantDetail(true);
  };

  const handleCreateTrainingSession = async () => {
    if (!newSessionForm.date_jour1 || !newSessionForm.date_jour2) {
      toast.error('Veuillez renseigner les dates');
      return;
    }
    try {
      await axios.post(`${API}/training-sessions`, newSessionForm);
      toast.success('Créneau créé');
      setShowCreateSessionModal(false);
      setNewSessionForm({
        date_jour1: '', date_jour2: '', heure_debut: '09:00', heure_fin: '17:00',
        places_total: 8, tarif: 150, lieu: 'Zoom', description: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleToggleSessionStatus = async (sessionId) => {
    try {
      await axios.post(`${API}/training-sessions/${sessionId}/toggle-status`);
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTrainingSession = async (sessionId) => {
    if (!window.confirm('Supprimer ce créneau ?')) return;
    try {
      await axios.delete(`${API}/training-sessions/${sessionId}`);
      toast.success('Créneau supprimé');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const autoFillJour2 = (jour1) => {
    if (!jour1) return;
    const date = new Date(jour1 + 'T00:00:00');
    date.setDate(date.getDate() + 1);
    const jour2 = date.toISOString().split('T')[0];
    setNewSessionForm(prev => ({ ...prev, date_jour1: jour1, date_jour2: jour2 }));
  };

  const getSessionStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-green-100', text: 'text-green-700', label: 'Ouvert' },
      closed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Fermé' },
      full: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Complet' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Annulé' },
    };
    const badge = badges[status] || badges.closed;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const copyStudentLink = (token) => {
    const link = `${window.location.origin}/student/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié dans le presse-papier');
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'Payé', icon: CheckCircle },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente', icon: Clock },
      refunded: { bg: 'bg-red-100', text: 'text-red-700', label: 'Remboursé', icon: AlertCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${badge.bg} ${badge.text}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  const getQuestionnaireById = (id) => {
    return questionnaires.find(q => q.id === id);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-body text-brown-mid">Chargement...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-body text-brown-mid">Chargement...</p>
      </div>
    );
  }

  // Session Detail View
  if (showSessionDetail && selectedSessionDetail) {
    const sd = selectedSessionDetail;
    const stats = sd.stats || {};
    const sessionParticipants = sd.participants || [];
    
    return (
      <div className="min-h-screen bg-cream" data-testid="session-detail-view">
        <header className="bg-white border-b border-sand px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setShowSessionDetail(false); setSelectedSessionDetail(null); }}
              className="flex items-center gap-2 text-brown-mid hover:text-brown transition-colors"
              data-testid="back-from-session-detail"
            >
              <ArrowLeft size={18} />
              <span className="font-body text-sm">Retour</span>
            </button>
          </div>
          <div className="flex items-center gap-3">
            {getSessionStatusBadge(sd.status)}
            <button
              onClick={async () => { await handleToggleSessionStatus(sd.id); fetchSessionDetail(sd.id); }}
              className={`flex items-center gap-2 px-3 py-2 border transition-colors ${sd.status === 'open' ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}
              data-testid="toggle-session-detail"
            >
              {sd.status === 'open' ? <Lock size={16} /> : <Unlock size={16} />}
              <span className="font-body text-sm">{sd.status === 'open' ? 'Fermer' : 'Ouvrir'}</span>
            </button>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-8 py-8">
          {/* Session Header */}
          <div className="bg-white border border-sand p-6 mb-6">
            <h1 className="font-display text-3xl text-brown mb-2">
              Lun. {new Date(sd.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — Mar. {new Date(sd.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </h1>
            <div className="flex items-center gap-6 font-body text-sm text-brown-mid mt-2">
              <span className="flex items-center gap-1"><Clock size={14} /> {sd.heure_debut} — {sd.heure_fin}</span>
              <span className="flex items-center gap-1"><MapPin size={14} /> {sd.lieu}</span>
              <span className="flex items-center gap-1"><Euro size={14} /> {sd.tarif} € / participant</span>
            </div>
            {sd.description && <p className="font-body text-sm text-brown-light mt-2">{sd.description}</p>}
          </div>

          {/* Session Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-terra">{stats.total || 0}</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">Inscrits</p>
            </div>
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-brown">{sd.places_total}</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">Places</p>
            </div>
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-green-600">{stats.paid || 0}</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">Payés</p>
            </div>
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-yellow-600">{stats.pending || 0}</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">En attente</p>
            </div>
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-brown">{stats.qcm_jour1_completed || 0}</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">QCM J1</p>
            </div>
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-brown">{stats.qcm_jour2_completed || 0}</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">QCM J2</p>
            </div>
            <div className="bg-white border border-sand p-4 text-center">
              <p className="font-display text-2xl font-semibold text-green-600">{stats.revenue || 0} €</p>
              <p className="font-body text-[10px] text-brown-light uppercase tracking-wider mt-1">CA</p>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="bg-white border border-sand p-6 mb-6">
            <h3 className="font-display text-lg text-brown mb-4">Progression de la session</h3>
            <div className="space-y-4">
              {[
                { label: 'Inscriptions', value: stats.total || 0, max: sd.places_total, color: 'bg-terra' },
                { label: 'Paiements', value: stats.paid || 0, max: stats.total || 1, color: 'bg-green-500' },
                { label: 'QCM Jour 1', value: stats.qcm_jour1_completed || 0, max: stats.total || 1, color: 'bg-blue-500' },
                { label: 'QCM Jour 2', value: stats.qcm_jour2_completed || 0, max: stats.total || 1, color: 'bg-blue-700' },
                { label: 'Attestations', value: stats.certificates || 0, max: stats.total || 1, color: 'bg-yellow-600' },
              ].map(({ label, value, max, color }) => (
                <div key={label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-body text-sm text-brown">{label}</span>
                    <span className="font-body text-sm font-medium text-brown">{value}/{max} ({max > 0 ? Math.round((value / max) * 100) : 0}%)</span>
                  </div>
                  <div className="w-full h-2 bg-sand">
                    <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Participants Table */}
          <div className="bg-white border border-sand">
            <div className="px-6 py-4 border-b border-sand">
              <h3 className="font-display text-lg text-brown">
                Participants ({sessionParticipants.length})
              </h3>
            </div>
            {sessionParticipants.length === 0 ? (
              <div className="p-8 text-center">
                <Users size={32} className="mx-auto text-sand mb-2" />
                <p className="font-body text-sm text-brown-mid">Aucun participant inscrit à cette session</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="session-participants-table">
                  <thead className="bg-cream-dark border-b border-sand">
                    <tr>
                      <th className="form-label px-4 py-3 text-left">NOM</th>
                      <th className="form-label px-4 py-3 text-left">EMAIL</th>
                      <th className="form-label px-4 py-3 text-left">RÉSEAU</th>
                      <th className="form-label px-4 py-3 text-center">PAIEMENT</th>
                      <th className="form-label px-4 py-3 text-center">QCM J1</th>
                      <th className="form-label px-4 py-3 text-center">QCM J2</th>
                      <th className="form-label px-4 py-3 text-center">QUEST.</th>
                      <th className="form-label px-4 py-3 text-center">CONTRAT</th>
                      <th className="form-label px-4 py-3 text-center">ATTESTATION</th>
                      <th className="form-label px-4 py-3 text-center">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionParticipants.map((sp) => (
                      <tr key={sp.id} className="border-b border-sand/50 hover:bg-cream/50">
                        <td className="px-4 py-3 font-body text-sm text-brown font-medium">{sp.prenom}</td>
                        <td className="px-4 py-3 font-body text-xs text-brown-mid">{sp.email}</td>
                        <td className="px-4 py-3 font-body text-xs text-brown-mid">{sp.reseau || '—'}</td>
                        <td className="px-4 py-3 text-center">{getPaymentStatusBadge(sp.payment_status)}</td>
                        <td className="px-4 py-3 text-center">
                          {sp.qcm_jour1_completed 
                            ? <button onClick={() => fetchQcmDetails(sp.id, sp.prenom)} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer" data-testid={`qcm1-score-${sp.id}`}><CheckCircle size={12} /> {sp.qcm_jour1_score_percent != null ? `${sp.qcm_jour1_score_percent}%` : 'Fait'}</button>
                            : <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-500"><XCircle size={12} /> —</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          {sp.qcm_jour2_completed 
                            ? <button onClick={() => fetchQcmDetails(sp.id, sp.prenom)} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer" data-testid={`qcm2-score-${sp.id}`}><CheckCircle size={12} /> {sp.qcm_jour2_score_percent != null ? `${sp.qcm_jour2_score_percent}%` : 'Fait'}</button>
                            : <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-500"><XCircle size={12} /> —</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          {sp.questionnaire_milieu_completed 
                            ? <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700"><CheckCircle size={12} /></span>
                            : <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-500"><XCircle size={12} /></span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          {sp.contract_status === 'signed'
                            ? <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700"><CheckCircle size={12} /> Signé</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-600">En attente</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          {sp.certificate_generated 
                            ? <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700"><Award size={12} /> Oui</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-500">Non</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => fetchQcmDetails(sp.id, sp.prenom)}
                              className="p-1 text-brown-mid hover:text-terra transition-colors"
                              title="Voir QCM & questionnaires"
                              data-testid={`view-qcm-${sp.id}`}
                            >
                              <FileText size={16} />
                            </button>
                            <button
                              onClick={() => { openParticipantDetail(sp); setShowSessionDetail(false); }}
                              className="p-1 text-terra hover:text-brown transition-colors"
                              title="Voir la fiche"
                              data-testid={`view-participant-${sp.id}`}
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* QCM & Questionnaire Detail Modal for Session Detail View */}
        <Dialog open={showQcmDetailModal} onOpenChange={setShowQcmDetailModal}>
          <DialogContent className="bg-white border border-sand p-0 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="qcm-detail-modal">
            <DialogHeader className="px-6 py-4 border-b border-sand sticky top-0 bg-white z-10">
              <DialogTitle className="font-display text-xl text-brown">
                Réponses de {qcmDetailData?.participantName}
              </DialogTitle>
              <DialogDescription className="sr-only">Détails des QCMs et questionnaires</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-6">
              {qcmDetailData?.qcms?.length > 0 && qcmDetailData.qcms.map((qcm) => (
                <div key={qcm.qcm_id} className="border border-sand">
                  <div className="bg-cream-dark px-4 py-3 flex items-center justify-between">
                    <h4 className="font-display text-lg text-brown">QCM Jour {qcm.qcm_day}</h4>
                    <span className={`font-display text-lg font-semibold ${qcm.score_percent >= 70 ? 'text-green-600' : 'text-red-500'}`}>
                      {qcm.score}/{qcm.total} ({qcm.score_percent}%)
                    </span>
                  </div>
                  <div className="divide-y divide-sand/50">
                    {qcm.questions?.map((q, i) => (
                      <div key={i} className={`px-4 py-3 ${q.is_correct ? 'bg-green-50/50' : 'bg-red-50/50'}`}>
                        <div className="flex items-start gap-3">
                          <span className={`mt-0.5 flex-shrink-0 ${q.is_correct ? 'text-green-600' : 'text-red-500'}`}>
                            {q.is_correct ? <CheckCircle size={16} /> : <XCircle size={16} />}
                          </span>
                          <div className="flex-1">
                            <p className="font-body text-sm text-brown mb-1">
                              <strong>Q{i + 1}.</strong> {q.text}
                            </p>
                            <div className="flex items-center gap-4 font-body text-xs">
                              <span className={q.is_correct ? 'text-green-700' : 'text-red-600'}>
                                Réponse : {q.user_answer || '—'}
                              </span>
                              {!q.is_correct && (
                                <span className="text-green-700">
                                  Correcte : {q.correct_answer}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {qcmDetailData?.questionnaires?.length > 0 && (
                <>
                  <h4 className="font-display text-lg text-brown mt-6">Questionnaires</h4>
                  {qcmDetailData.questionnaires.map((quest) => (
                    <div key={quest.questionnaire_id} className="border border-sand">
                      <div className="bg-cream-dark px-4 py-3">
                        <h5 className="font-display text-base text-brown">{quest.questionnaire_title}</h5>
                        <span className="font-body text-xs text-brown-light">
                          {quest.questionnaire_type === 'milieu' ? 'Mi-parcours' : 'Fin de formation'} · {quest.submitted_at ? new Date(quest.submitted_at).toLocaleDateString('fr-FR') : ''}
                        </span>
                      </div>
                      <div className="divide-y divide-sand/50">
                        {quest.answers?.map((a, i) => (
                          <div key={i} className="px-4 py-3">
                            <p className="font-body text-sm font-medium text-brown mb-1">{a.question}</p>
                            <p className="font-body text-sm text-terra">{a.answer || '—'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {(!qcmDetailData?.qcms?.length && !qcmDetailData?.questionnaires?.length) && (
                <p className="font-body text-sm text-brown-mid text-center py-8">Aucune réponse enregistrée pour ce participant</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Participant Detail View
  if (showParticipantDetail && participantDetail) {
    const p = participantDetail.participant;
    const pAnswers = participantDetail.answers;
    const pNotes = participantDetail.notes;
    
    return (
      <div className="min-h-screen bg-cream">
        {/* Header */}
        <header className="bg-white border-b border-sand px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowParticipantDetail(false)}
              className="flex items-center gap-2 text-brown-mid hover:text-brown transition-colors"
              data-testid="back-to-list"
            >
              <ArrowLeft size={18} />
              <span className="font-body text-sm">Retour</span>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => copyStudentLink(p.token)}
              className="flex items-center gap-2 px-3 py-2 bg-cream border border-sand hover:bg-sand/30 transition-colors"
              data-testid="copy-student-link"
            >
              <LinkIcon size={16} className="text-terra" />
              <span className="font-body text-sm">Copier le lien</span>
            </button>
            <button
              onClick={() => handleDeleteParticipant(p.id)}
              className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 transition-colors"
              data-testid="delete-participant-detail"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-8 py-8">
          {/* Participant Header */}
          <div className="bg-white border border-sand p-6 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="font-display text-3xl text-brown mb-2">{p.prenom}</h1>
                <p className="font-body text-brown-mid">{p.email}</p>
                <p className="font-body text-sm text-brown-light">{p.reseau || 'Réseau non renseigné'}</p>
              </div>
              <div className="text-right">
                {getPaymentStatusBadge(p.payment_status)}
                <p className="font-body text-xs text-brown-light mt-2">
                  Inscrit le {new Date(p.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Info & Actions */}
            <div className="space-y-6">
              {/* Payment Card */}
              <div className="bg-white border border-sand p-6" data-testid="payment-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg text-brown">Paiement</h3>
                  <CreditCard size={20} className="text-terra" />
                </div>
                
                {p.payment_status === 'paid' ? (
                  <div className="space-y-2">
                    <p className="font-display text-2xl text-terra font-semibold">{p.payment_amount} €</p>
                    <p className="font-body text-sm text-brown-mid">
                      Payé le {new Date(p.payment_date).toLocaleDateString('fr-FR')}
                    </p>
                    {p.payment_reference && (
                      <p className="font-body text-xs text-brown-light">Réf: {p.payment_reference}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="font-body text-sm text-brown-mid mb-4">Paiement en attente</p>
                    <button
                      onClick={() => {
                        setPaymentForm({ amount: session?.tarif || 150, reference: '' });
                        setShowPaymentModal(true);
                      }}
                      className="cta-button w-full flex items-center justify-center gap-2"
                      data-testid="mark-payment-btn"
                    >
                      <CheckCircle size={16} />
                      Marquer comme payé
                    </button>
                  </div>
                )}
              </div>

              {/* Progress Card */}
              <div className="bg-white border border-sand p-6" data-testid="progress-card">
                <h3 className="font-display text-lg text-brown mb-4">Progression</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center ${p.questionnaire_milieu_completed ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                      {p.questionnaire_milieu_completed ? <CheckCircle size={16} /> : <Clock size={16} />}
                    </div>
                    <div>
                      <p className="font-body text-sm text-brown">Questionnaire mi-parcours</p>
                      <p className="font-body text-xs text-brown-light">
                        {p.questionnaire_milieu_completed ? 'Complété' : 'En attente'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center ${p.questionnaire_fin_completed ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                      {p.questionnaire_fin_completed ? <CheckCircle size={16} /> : <Clock size={16} />}
                    </div>
                    <div>
                      <p className="font-body text-sm text-brown">Questionnaire fin</p>
                      <p className="font-body text-xs text-brown-light">
                        {p.questionnaire_fin_completed ? 'Complété' : 'En attente'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center ${p.certificate_generated ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                      {p.certificate_generated ? <FileCheck size={16} /> : <FileText size={16} />}
                    </div>
                    <div>
                      <p className="font-body text-sm text-brown">Attestation</p>
                      <p className="font-body text-xs text-brown-light">
                        {p.certificate_generated ? `Générée le ${new Date(p.certificate_date).toLocaleDateString('fr-FR')}` : 'Non générée'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-white border border-sand p-6">
                <h3 className="font-display text-lg text-brown mb-4">Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowEmailModal(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-cream hover:bg-sand/30 transition-colors text-left"
                    data-testid="send-email-btn"
                  >
                    <Mail size={18} className="text-terra" />
                    <span className="font-body text-sm text-brown">Envoyer un email</span>
                  </button>
                  <button
                    onClick={() => setShowNoteModal(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-cream hover:bg-sand/30 transition-colors text-left"
                    data-testid="add-note-btn"
                  >
                    <MessageSquare size={18} className="text-brown-mid" />
                    <span className="font-body text-sm text-brown">Ajouter une note</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - History & Answers */}
            <div className="lg:col-span-2 space-y-6">
              {/* Questionnaire Answers */}
              <div className="bg-white border border-sand p-6" data-testid="answers-section">
                <h3 className="font-display text-lg text-brown mb-4">Réponses aux questionnaires</h3>
                
                {pAnswers.length === 0 ? (
                  <p className="font-body text-sm text-brown-mid">Aucune réponse enregistrée</p>
                ) : (
                  <div className="space-y-4">
                    {pAnswers.map((answerSet) => {
                      const questionnaire = getQuestionnaireById(answerSet.questionnaire_id);
                      return (
                        <div key={answerSet.id} className="border border-sand p-4" data-testid={`answer-set-${answerSet.id}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-display text-base text-brown">
                              {questionnaire?.title || 'Questionnaire'}
                            </h4>
                            <span className={`px-2 py-1 text-xs ${
                              answerSet.questionnaire_type === 'milieu' ? 'bg-terra/10 text-terra' : 'bg-brown/10 text-brown'
                            }`}>
                              {answerSet.questionnaire_type === 'milieu' ? 'Mi-parcours' : 'Fin'}
                            </span>
                          </div>
                          <div className="space-y-3">
                            {answerSet.answers.map((answer, i) => {
                              const question = questionnaire?.questions.find(q => q.id === answer.question_id);
                              return (
                                <div key={i} className="bg-cream p-3">
                                  <p className="font-body text-xs text-brown-mid mb-1">
                                    {question?.text || `Question ${i + 1}`}
                                  </p>
                                  <p className="font-body text-sm text-brown font-medium">
                                    {Array.isArray(answer.answer) ? answer.answer.join(', ') : answer.answer}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                          <p className="font-body text-xs text-brown-light mt-3">
                            Soumis le {new Date(answerSet.submitted_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* History/Notes */}
              <div className="bg-white border border-sand p-6" data-testid="history-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg text-brown">Historique & Notes</h3>
                  <button
                    onClick={() => setShowNoteModal(true)}
                    className="flex items-center gap-1 text-terra hover:text-terra-dark text-sm font-body"
                  >
                    <Plus size={14} />
                    Ajouter
                  </button>
                </div>
                
                {pNotes.length === 0 ? (
                  <p className="font-body text-sm text-brown-mid">Aucune note</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {pNotes.map((note) => {
                      const NoteIcon = NOTE_ICONS[note.type] || MessageSquare;
                      return (
                        <div key={note.id} className="flex gap-3 p-3 bg-cream border-l-2 border-terra/30">
                          <div className="w-8 h-8 bg-white flex items-center justify-center flex-shrink-0">
                            <NoteIcon size={14} className="text-brown-mid" />
                          </div>
                          <div className="flex-1">
                            <p className="font-body text-sm text-brown">{note.content}</p>
                            <p className="font-body text-xs text-brown-light mt-1">
                              {new Date(note.created_at).toLocaleDateString('fr-FR')} à {new Date(note.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="bg-white border border-sand p-0 max-w-md">
            <DialogHeader className="px-6 py-4 border-b border-sand">
              <DialogTitle className="font-display text-xl text-brown">Enregistrer un paiement</DialogTitle>
              <DialogDescription className="sr-only">Formulaire de paiement</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="form-label block mb-2">MONTANT (€)</label>
                <input
                  type="number"
                  className="form-input w-full"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
                  data-testid="payment-amount"
                />
              </div>
              <div>
                <label className="form-label block mb-2">RÉFÉRENCE (optionnel)</label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="Ex: Revolut #123456"
                  data-testid="payment-reference"
                />
              </div>
              <button
                onClick={handleMarkPayment}
                className="cta-button w-full flex items-center justify-center gap-2"
                data-testid="confirm-payment"
              >
                <CheckCircle size={16} />
                Confirmer le paiement
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Note Modal */}
        <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
          <DialogContent className="bg-white border border-sand p-0 max-w-md">
            <DialogHeader className="px-6 py-4 border-b border-sand">
              <DialogTitle className="font-display text-xl text-brown">Ajouter une note</DialogTitle>
              <DialogDescription className="sr-only">Formulaire de note</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="form-label block mb-2">TYPE</label>
                <select
                  className="form-input w-full"
                  value={noteForm.type}
                  onChange={(e) => setNoteForm({ ...noteForm, type: e.target.value })}
                  data-testid="note-type"
                >
                  <option value="note">Note</option>
                  <option value="call">Appel téléphonique</option>
                  <option value="email_received">Email reçu</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <div>
                <label className="form-label block mb-2">CONTENU</label>
                <textarea
                  className="form-input w-full min-h-[100px]"
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  placeholder="Votre note..."
                  data-testid="note-content"
                />
              </div>
              <button
                onClick={handleAddNote}
                className="cta-button w-full flex items-center justify-center gap-2"
                data-testid="save-note"
              >
                <Save size={16} />
                Enregistrer
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Modal */}
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="bg-white border border-sand p-0 max-w-lg">
            <DialogHeader className="px-6 py-4 border-b border-sand">
              <DialogTitle className="font-display text-xl text-brown">
                Envoyer un email à {p.prenom}
              </DialogTitle>
              <DialogDescription className="sr-only">Envoyer un email</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div className="bg-cream p-3">
                <p className="font-body text-xs text-brown-light">Destinataire</p>
                <p className="font-body text-sm text-brown">{p.email}</p>
              </div>
              <div>
                <label className="form-label block mb-2">OBJET</label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Objet de l'email"
                  data-testid="email-subject"
                />
              </div>
              <div>
                <label className="form-label block mb-2">MESSAGE</label>
                <textarea
                  className="form-input w-full min-h-[150px]"
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  placeholder="Votre message..."
                  data-testid="email-body"
                />
              </div>
              <button
                onClick={handleSendEmail}
                className="cta-button w-full flex items-center justify-center gap-2"
                data-testid="send-email"
              >
                <Send size={16} />
                Envoyer
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-sand px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={LOGO_TERRACOTTA} alt="L'Académie" className="h-8" data-testid="admin-logo" />
          <span className="font-display text-xl text-brown">
            Administration
          </span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-brown-mid hover:text-brown transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={18} />
          <span className="font-body text-sm">Déconnexion</span>
        </button>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-sand p-1 mb-6">
            <TabsTrigger 
              value="dashboard" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-dashboard"
            >
              <BarChart3 size={16} className="mr-2" />
              Tableau de bord
            </TabsTrigger>
            <TabsTrigger 
              value="participants" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-participants"
            >
              <Users size={16} className="mr-2" />
              Participants
            </TabsTrigger>
            <TabsTrigger 
              value="payments" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-payments"
            >
              <CreditCard size={16} className="mr-2" />
              Paiements
            </TabsTrigger>
            <TabsTrigger 
              value="sessions" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-sessions"
            >
              <CalendarDays size={16} className="mr-2" />
              Sessions
            </TabsTrigger>
            <TabsTrigger 
              value="qcms" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-qcms"
            >
              <ClipboardList size={16} className="mr-2" />
              QCMs
            </TabsTrigger>
            <TabsTrigger 
              value="calendrier" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-calendrier"
            >
              <Calendar size={16} className="mr-2" />
              Calendrier
            </TabsTrigger>
            <TabsTrigger 
              value="emails" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-emails"
            >
              <Mail size={16} className="mr-2" />
              Emails
            </TabsTrigger>
            <TabsTrigger 
              value="parametres" 
              className="font-body text-sm data-[state=active]:bg-brown data-[state=active]:text-cream px-6 py-2"
              data-testid="tab-parametres"
            >
              <Settings size={16} className="mr-2" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboardStats && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <button
                    onClick={() => setActiveTab('participants')}
                    className="bg-white border border-sand p-6 text-left hover:border-terra transition-colors"
                    data-testid="stat-participants"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="form-label">PARTICIPANTS</p>
                      <Users size={20} className="text-terra" />
                    </div>
                    <p className="font-display text-3xl font-semibold text-terra">{dashboardStats.participants.total}</p>
                    <p className="font-body text-xs text-brown-light mt-1">
                      {dashboardStats.participants.paid} payés · {dashboardStats.participants.pending_payment} en attente
                    </p>
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('sessions')}
                    className="bg-white border border-sand p-6 text-left hover:border-terra transition-colors"
                    data-testid="stat-sessions"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="form-label">SESSIONS</p>
                      <CalendarDays size={20} className="text-brown-mid" />
                    </div>
                    <p className="font-display text-3xl font-semibold text-brown">
                      {trainingSessions.filter(s => s.status === 'open').length}
                    </p>
                    <p className="font-body text-xs text-brown-light mt-1">
                      session{trainingSessions.filter(s => s.status === 'open').length > 1 ? 's' : ''} ouverte{trainingSessions.filter(s => s.status === 'open').length > 1 ? 's' : ''} · {trainingSessions.length} total
                    </p>
                  </button>

                  <button
                    onClick={() => setActiveTab('payments')}
                    className="bg-white border border-sand p-6 text-left hover:border-terra transition-colors"
                    data-testid="stat-revenue"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="form-label">CHIFFRE D&apos;AFFAIRES</p>
                      <TrendingUp size={20} className="text-green-600" />
                    </div>
                    <p className="font-display text-3xl font-semibold text-green-600">{dashboardStats.revenue.total} €</p>
                    <p className="font-body text-xs text-brown-light mt-1">
                      sur {dashboardStats.revenue.expected} € attendus
                    </p>
                  </button>

                  <div
                    className="bg-white border border-sand p-6 text-left"
                    data-testid="stat-completion"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="form-label">TAUX PAIEMENT</p>
                      <CheckCircle size={20} className="text-terra" />
                    </div>
                    <p className="font-display text-3xl font-semibold text-terra">
                      {dashboardStats.participants.total > 0 ? Math.round((dashboardStats.participants.paid / dashboardStats.participants.total) * 100) : 0}%
                    </p>
                    <p className="font-body text-xs text-brown-light mt-1">
                      {dashboardStats.participants.paid} / {dashboardStats.participants.total} participants
                    </p>
                  </div>
                </div>

                {/* Sessions Overview */}
                <div className="bg-white border border-sand p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg text-brown">Sessions à venir</h3>
                    <button
                      onClick={() => setActiveTab('sessions')}
                      className="text-terra text-sm font-body hover:underline"
                    >
                      Gérer les sessions →
                    </button>
                  </div>
                  
                  {trainingSessions.length === 0 ? (
                    <p className="font-body text-sm text-brown-mid py-4">Aucune session programmée. Créez votre premier créneau dans l&apos;onglet Sessions.</p>
                  ) : (
                    <div className="space-y-3">
                      {trainingSessions.map((ts) => (
                        <div
                          key={ts.id}
                          onClick={() => fetchSessionDetail(ts.id)}
                          className="flex items-center justify-between p-4 bg-cream hover:bg-sand/30 transition-colors cursor-pointer border border-transparent hover:border-terra/20"
                          data-testid={`dashboard-session-${ts.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white border border-sand flex flex-col items-center justify-center flex-shrink-0">
                              <span className="font-display text-lg font-semibold text-terra leading-none">
                                {new Date(ts.date_jour1 + 'T00:00:00').getDate()}
                              </span>
                              <span className="font-body text-[10px] text-brown-light uppercase">
                                {new Date(ts.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'short' })}
                              </span>
                            </div>
                            <div>
                              <p className="font-body text-sm font-medium text-brown">
                                Lun. {new Date(ts.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — Mar. {new Date(ts.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                              </p>
                              <p className="font-body text-xs text-brown-light">{ts.lieu} · {ts.heure_debut} — {ts.heure_fin}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="font-display text-lg font-semibold text-terra">{ts.places_prises}/{ts.places_total}</p>
                              <p className="font-body text-[10px] text-brown-light uppercase">inscrits</p>
                            </div>
                            <div className="text-right">
                              <p className="font-display text-lg font-semibold text-green-600">{ts.paid_count || 0}</p>
                              <p className="font-body text-[10px] text-brown-light uppercase">payés</p>
                            </div>
                            <div className="text-right">
                              <p className="font-display text-lg font-semibold text-brown">{ts.revenue || 0} €</p>
                              <p className="font-body text-[10px] text-brown-light uppercase">CA</p>
                            </div>
                            {getSessionStatusBadge(ts.status)}
                            <Eye size={16} className="text-brown-light" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Participants */}
                <div className="bg-white border border-sand p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg text-brown">Dernières inscriptions</h3>
                    <button
                      onClick={() => setActiveTab('participants')}
                      className="text-terra text-sm font-body hover:underline"
                    >
                      Voir tout →
                    </button>
                  </div>
                  <div className="space-y-3">
                    {dashboardStats.recent_participants.map((p) => (
                      <div 
                        key={p.id}
                        onClick={() => openParticipantDetail(p)}
                        className="flex items-center justify-between p-3 bg-cream hover:bg-sand/30 transition-colors cursor-pointer"
                      >
                        <div>
                          <p className="font-body text-sm font-medium text-brown">{p.prenom}</p>
                          <p className="font-body text-xs text-brown-light">{p.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getPaymentStatusBadge(p.payment_status)}
                          <span className="font-body text-xs text-brown-light">
                            {new Date(p.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    ))}
                    {dashboardStats.recent_participants.length === 0 && (
                      <p className="font-body text-sm text-brown-mid">Aucun participant inscrit</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Participants Tab */}
          <TabsContent value="participants">
            <div className="bg-white border border-sand" data-testid="participants-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cream-dark border-b border-sand">
                    <tr>
                      <th className="form-label px-4 py-3 text-left">PRÉNOM</th>
                      <th className="form-label px-4 py-3 text-left">EMAIL</th>
                      <th className="form-label px-4 py-3 text-left">RÉSEAU</th>
                      <th className="form-label px-4 py-3 text-left">PAIEMENT</th>
                      <th className="form-label px-4 py-3 text-left">PROGRESSION</th>
                      <th className="form-label px-4 py-3 text-left">DATE</th>
                      <th className="form-label px-4 py-3 text-right">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr 
                        key={p.id} 
                        className="border-b border-sand/50 hover:bg-cream/50 transition-colors cursor-pointer" 
                        onClick={() => openParticipantDetail(p)}
                        data-testid={`participant-row-${p.id}`}
                      >
                        <td className="px-4 py-4 font-body text-sm text-brown font-medium">{p.prenom}</td>
                        <td className="px-4 py-4 font-body text-sm text-brown-mid">{p.email}</td>
                        <td className="px-4 py-4 font-body text-sm text-brown-mid">{p.reseau || '—'}</td>
                        <td className="px-4 py-4">
                          {getPaymentStatusBadge(p.payment_status)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <span className={`inline-block w-2 h-2 ${p.questionnaire_milieu_completed ? 'bg-green-500' : 'bg-sand'}`} title="Questionnaire milieu" />
                            <span className={`inline-block w-2 h-2 ${p.questionnaire_fin_completed ? 'bg-green-500' : 'bg-sand'}`} title="Questionnaire fin" />
                            <span className={`inline-block w-2 h-2 ${p.certificate_generated ? 'bg-terra' : 'bg-sand'}`} title="Attestation" />
                          </div>
                        </td>
                        <td className="px-4 py-4 font-body text-xs text-brown-light">
                          {new Date(p.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => copyStudentLink(p.token)}
                              className="p-2 hover:bg-cream transition-colors"
                              title="Copier le lien élève"
                              data-testid={`copy-link-${p.id}`}
                            >
                              <LinkIcon size={16} className="text-terra" />
                            </button>
                            <button
                              onClick={() => openParticipantDetail(p)}
                              className="p-2 hover:bg-cream transition-colors"
                              title="Voir la fiche"
                              data-testid={`view-participant-${p.id}`}
                            >
                              <Eye size={16} className="text-brown-mid" />
                            </button>
                            <button
                              onClick={() => handleDeleteParticipant(p.id)}
                              className="p-2 hover:bg-cream transition-colors"
                              title="Supprimer"
                              data-testid={`delete-participant-${p.id}`}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {participants.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center font-body text-brown-mid">
                          Aucun participant inscrit
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-sand p-6">
                <p className="form-label mb-1">TOTAL ENCAISSÉ</p>
                <p className="font-display text-3xl font-semibold text-green-600">
                  {participants.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + (p.payment_amount || 0), 0)} €
                </p>
              </div>
              <div className="bg-white border border-sand p-6">
                <p className="form-label mb-1">EN ATTENTE</p>
                <p className="font-display text-3xl font-semibold text-yellow-600">
                  {participants.filter(p => p.payment_status === 'pending').length * (session?.tarif || 150)} €
                </p>
                <p className="font-body text-xs text-brown-light mt-1">
                  {participants.filter(p => p.payment_status === 'pending').length} participant(s)
                </p>
              </div>
              <div className="bg-white border border-sand p-6">
                <p className="form-label mb-1">TAUX DE CONVERSION</p>
                <p className="font-display text-3xl font-semibold text-terra">
                  {participants.length > 0 ? Math.round((participants.filter(p => p.payment_status === 'paid').length / participants.length) * 100) : 0}%
                </p>
              </div>
            </div>

            <div className="bg-white border border-sand" data-testid="payments-table">
              <div className="px-6 py-4 border-b border-sand">
                <h3 className="font-display text-lg text-brown">Historique des paiements</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cream-dark border-b border-sand">
                    <tr>
                      <th className="form-label px-4 py-3 text-left">PARTICIPANT</th>
                      <th className="form-label px-4 py-3 text-left">STATUT</th>
                      <th className="form-label px-4 py-3 text-left">MONTANT</th>
                      <th className="form-label px-4 py-3 text-left">RÉFÉRENCE</th>
                      <th className="form-label px-4 py-3 text-left">DATE</th>
                      <th className="form-label px-4 py-3 text-right">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.id} className="border-b border-sand/50 hover:bg-cream/50 transition-colors">
                        <td className="px-4 py-4">
                          <p className="font-body text-sm text-brown font-medium">{p.prenom}</p>
                          <p className="font-body text-xs text-brown-light">{p.email}</p>
                        </td>
                        <td className="px-4 py-4">
                          {getPaymentStatusBadge(p.payment_status)}
                        </td>
                        <td className="px-4 py-4 font-body text-sm text-brown">
                          {p.payment_amount ? `${p.payment_amount} €` : '—'}
                        </td>
                        <td className="px-4 py-4 font-body text-xs text-brown-light">
                          {p.payment_reference || '—'}
                        </td>
                        <td className="px-4 py-4 font-body text-xs text-brown-light">
                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-4 py-4 text-right">
                          {p.payment_status === 'pending' && (
                            <button
                              onClick={() => {
                                setSelectedParticipant(p);
                                setPaymentForm({ amount: session?.tarif || 150, reference: '' });
                                setShowPaymentModal(true);
                              }}
                              className="text-terra text-sm font-body hover:underline"
                            >
                              Marquer payé
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Questionnaires Tab */}
          <TabsContent value="questionnaires">
            <div className="mb-4">
              <button
                onClick={() => openQuestionnaireEdit()}
                className="cta-button flex items-center gap-2"
                data-testid="add-questionnaire-button"
              >
                <Plus size={16} />
                Nouveau questionnaire
              </button>
            </div>

            <div className="space-y-4">
              {questionnaires.map((q) => (
                <div key={q.id} className="bg-white border border-sand p-6" data-testid={`questionnaire-card-${q.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className={`inline-block px-2 py-1 text-xs font-body uppercase tracking-wider ${
                        q.type === 'milieu' ? 'bg-terra/10 text-terra' : 'bg-brown/10 text-brown'
                      }`}>
                        {q.type === 'milieu' ? 'Mi-parcours' : 'Fin de formation'}
                      </span>
                      <h3 className="font-display text-xl text-brown mt-2">{q.title}</h3>
                      <p className="font-body text-sm text-brown-mid mt-1">
                        {q.questions.length} question{q.questions.length > 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openQuestionnaireEdit(q)}
                        className="p-2 hover:bg-cream transition-colors"
                        data-testid={`edit-questionnaire-${q.id}`}
                      >
                        <Edit size={18} className="text-brown-mid" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestionnaire(q.id)}
                        className="p-2 hover:bg-cream transition-colors"
                        data-testid={`delete-questionnaire-${q.id}`}
                      >
                        <Trash2 size={18} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Questions preview */}
                  <div className="mt-4 space-y-2">
                    {q.questions.slice(0, 3).map((question, i) => (
                      <p key={i} className="font-body text-sm text-brown-light">
                        {i + 1}. {question.text}
                      </p>
                    ))}
                    {q.questions.length > 3 && (
                      <p className="font-body text-xs text-terra">
                        + {q.questions.length - 3} autres questions
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCreateSessionModal(true)}
                  className="cta-button flex items-center gap-2"
                  data-testid="add-session-button"
                >
                  <Plus size={16} />
                  Nouveau créneau
                </button>
                <label className="flex items-center gap-2 font-body text-sm text-brown-mid cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showIncludePast}
                    onChange={(e) => {
                      setShowIncludePast(e.target.checked);
                    }}
                    className="accent-terra"
                    data-testid="toggle-past-sessions"
                  />
                  Afficher les sessions passées
                </label>
              </div>
            </div>

            {trainingSessions.length === 0 ? (
              <div className="bg-white border border-sand p-12 text-center">
                <CalendarDays size={48} className="mx-auto text-sand mb-4" />
                <p className="font-display text-xl text-brown mb-2">Aucun créneau</p>
                <p className="font-body text-sm text-brown-mid">Créez votre premier créneau de formation lundi-mardi.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {trainingSessions.map((ts) => (
                  <div key={ts.id} className="bg-white border border-sand p-6 cursor-pointer hover:border-terra/30 transition-colors" data-testid={`session-card-${ts.id}`} onClick={() => fetchSessionDetail(ts.id)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getSessionStatusBadge(ts.status)}
                          <span className="font-body text-xs text-brown-light">
                            Créé le {new Date(ts.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <h3 className="font-display text-xl text-brown mb-1">
                          Lun. {new Date(ts.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — Mar. {new Date(ts.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </h3>
                        <div className="flex items-center gap-6 mt-2">
                          <span className="font-body text-sm text-brown-mid flex items-center gap-1">
                            <Clock size={14} /> {ts.heure_debut} — {ts.heure_fin}
                          </span>
                          <span className="font-body text-sm text-brown-mid flex items-center gap-1">
                            <MapPin size={14} /> {ts.lieu}
                          </span>
                          <span className="font-body text-sm text-brown-mid flex items-center gap-1">
                            <Euro size={14} /> {ts.tarif} €
                          </span>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-body text-xs text-brown-light uppercase tracking-wider">Inscriptions</span>
                            <span className="font-body text-sm font-medium text-terra">
                              {ts.places_prises} / {ts.places_total}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-sand">
                            <div
                              className="h-full bg-terra transition-all duration-300"
                              style={{ width: `${ts.places_total > 0 ? (ts.places_prises / ts.places_total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        {ts.description && (
                          <p className="font-body text-sm text-brown-mid mt-2">{ts.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleSessionStatus(ts.id); }}
                          className={`p-2 hover:bg-cream transition-colors ${ts.status === 'open' ? 'text-red-500' : 'text-green-600'}`}
                          title={ts.status === 'open' ? 'Fermer les inscriptions' : 'Ouvrir les inscriptions'}
                          data-testid={`toggle-session-${ts.id}`}
                        >
                          {ts.status === 'open' ? <Lock size={18} /> : <Unlock size={18} />}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteTrainingSession(ts.id); }}
                          className="p-2 hover:bg-cream transition-colors text-red-500"
                          title="Supprimer"
                          data-testid={`delete-session-${ts.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* QCMs Tab */}
          <TabsContent value="qcms">
            {/* QCM Stats Modal */}
            {showQcmStats && qcmStats ? (
              <div data-testid="qcm-stats-view">
                <button
                  onClick={() => setShowQcmStats(false)}
                  className="flex items-center gap-2 text-brown-mid hover:text-brown transition-colors mb-4"
                  data-testid="back-from-qcm-stats"
                >
                  <ArrowLeft size={18} />
                  <span className="font-body text-sm">Retour aux QCMs</span>
                </button>

                <div className="bg-white border border-sand p-6 mb-6">
                  <h2 className="font-display text-2xl text-brown mb-1">{qcmStats.title}</h2>
                  <div className="flex items-center gap-6 font-body text-sm text-brown-mid">
                    <span>{qcmStats.total_questions} questions</span>
                    <span>{qcmStats.total_submissions} soumissions</span>
                    <span>Score moyen : <strong className="text-terra">{qcmStats.avg_score}%</strong></span>
                    <span>Taux de réussite : <strong className={qcmStats.pass_rate >= 70 ? 'text-green-600' : 'text-red-500'}>{qcmStats.pass_rate}%</strong></span>
                  </div>
                </div>

                {/* Per-question success rates */}
                <div className="bg-white border border-sand mb-6">
                  <div className="px-6 py-4 border-b border-sand">
                    <h3 className="font-display text-lg text-brown">Taux de réussite par question</h3>
                  </div>
                  <div className="divide-y divide-sand/50">
                    {qcmStats.question_stats?.map((qs) => (
                      <div key={qs.index} className="px-6 py-3 flex items-center gap-4">
                        <span className="font-body text-xs text-brown-light w-8 flex-shrink-0">Q{qs.index + 1}</span>
                        <div className="flex-1">
                          <p className="font-body text-sm text-brown mb-1 line-clamp-1">{qs.text}</p>
                          <div className="w-full h-2 bg-sand">
                            <div
                              className={`h-full transition-all ${qs.success_rate >= 70 ? 'bg-green-500' : qs.success_rate >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${qs.success_rate}%` }}
                            />
                          </div>
                        </div>
                        <span className={`font-body text-sm font-medium w-16 text-right ${qs.success_rate >= 70 ? 'text-green-600' : qs.success_rate >= 40 ? 'text-yellow-600' : 'text-red-500'}`}>
                          {qs.success_rate}%
                        </span>
                        <span className="font-body text-xs text-brown-light w-20 text-right">
                          {qs.correct_count}/{qs.total_answered}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Student results */}
                <div className="bg-white border border-sand">
                  <div className="px-6 py-4 border-b border-sand">
                    <h3 className="font-display text-lg text-brown">Résultats par élève ({qcmStats.student_results?.length})</h3>
                  </div>
                  {qcmStats.student_results?.length === 0 ? (
                    <p className="px-6 py-8 text-center font-body text-sm text-brown-mid">Aucune soumission</p>
                  ) : (
                    <div className="divide-y divide-sand/50">
                      {qcmStats.student_results?.map((sr) => (
                        <div key={sr.participant_id} className="px-6 py-3 flex items-center justify-between">
                          <div>
                            <p className="font-body text-sm font-medium text-brown">{sr.participant_name}</p>
                            <p className="font-body text-xs text-brown-light">{sr.participant_email}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-body text-xs text-brown-light">
                              {sr.submitted_at ? new Date(sr.submitted_at).toLocaleDateString('fr-FR') : ''}
                            </span>
                            <span className={`font-display text-lg font-semibold ${sr.passed ? 'text-green-600' : 'text-red-500'}`}>
                              {sr.score}/{sr.total}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs ${sr.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                              {sr.score_percent}%
                            </span>
                            <button
                              onClick={() => fetchQcmDetails(sr.participant_id, sr.participant_name)}
                              className="p-1 text-terra hover:text-brown transition-colors"
                              title="Voir les réponses"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* QCM List & Editor */
              <div className="space-y-6">
                {adminQcms.map((qcm) => (
                  <div key={qcm.id} className="bg-white border border-sand" data-testid={`qcm-card-${qcm.id}`}>
                    {/* QCM Header */}
                    <div className="px-6 py-4 border-b border-sand flex items-center justify-between">
                      <div>
                        <h3 className="font-display text-xl text-brown">{qcm.title}</h3>
                        <div className="flex items-center gap-4 mt-1 font-body text-xs text-brown-light">
                          <span>Jour {qcm.day}</span>
                          <span>{qcm.questions?.length || 0} questions</span>
                          <span>{qcm.total_submissions} soumissions</span>
                          {qcm.total_submissions > 0 && (
                            <>
                              <span>Score moyen : <strong className="text-terra">{qcm.avg_score_percent}%</strong></span>
                              <span>Réussite : <strong className={qcm.pass_rate >= 70 ? 'text-green-600' : 'text-red-500'}>{qcm.pass_rate}%</strong></span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchQcmStats(qcm.id)}
                          className="flex items-center gap-2 px-3 py-2 border border-sand hover:bg-cream transition-colors"
                          data-testid={`qcm-stats-${qcm.id}`}
                        >
                          <BarChart3 size={16} className="text-terra" />
                          <span className="font-body text-sm">Statistiques</span>
                        </button>
                        <button
                          onClick={() => addNewQuestion(qcm.id)}
                          className="flex items-center gap-2 px-3 py-2 border border-sand hover:bg-cream transition-colors"
                          data-testid={`qcm-add-question-${qcm.id}`}
                        >
                          <Plus size={16} className="text-green-600" />
                          <span className="font-body text-sm">Ajouter</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Questions List */}
                    <div className="divide-y divide-sand/50">
                      {qcm.questions?.map((q, qIdx) => (
                        <div key={qIdx} className="px-6 py-3">
                          {editingQcm === qcm.id && editingQuestionIdx === qIdx ? (
                            /* Editing Mode */
                            <div className="space-y-3" data-testid={`question-edit-${qIdx}`}>
                              <textarea
                                className="form-input w-full min-h-[60px]"
                                value={qcmEditForm.text}
                                onChange={(e) => setQcmEditForm({ ...qcmEditForm, text: e.target.value })}
                                data-testid="edit-question-text"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                {['A', 'B', 'C', 'D'].map((letter, i) => (
                                  <div key={letter} className="flex items-center gap-2">
                                    <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 ${qcmEditForm.correct === letter ? 'bg-green-500 text-white' : 'bg-sand text-brown-mid'}`}>{letter}</span>
                                    <input
                                      className="form-input flex-1 text-sm"
                                      value={qcmEditForm.options[i]}
                                      onChange={(e) => {
                                        const newOptions = [...qcmEditForm.options];
                                        newOptions[i] = e.target.value;
                                        setQcmEditForm({ ...qcmEditForm, options: newOptions });
                                      }}
                                      data-testid={`edit-option-${letter}`}
                                    />
                                    <button
                                      onClick={() => setQcmEditForm({ ...qcmEditForm, correct: letter })}
                                      className={`p-1 ${qcmEditForm.correct === letter ? 'text-green-600' : 'text-gray-300 hover:text-green-400'}`}
                                      title="Marquer comme correcte"
                                    >
                                      <CheckCircle size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={saveQuestion} className="cta-button text-sm py-1 px-4 flex items-center gap-1" data-testid="save-question-btn"><Save size={14} /> Sauvegarder</button>
                                <button onClick={() => setEditingQuestionIdx(null)} className="px-4 py-1 border border-sand text-sm hover:bg-cream transition-colors"><X size={14} /></button>
                              </div>
                            </div>
                          ) : (
                            /* Display Mode */
                            <div className="flex items-start justify-between group">
                              <div className="flex-1">
                                <p className="font-body text-sm text-brown">
                                  <span className="text-brown-light mr-2">Q{qIdx + 1}.</span>
                                  {q.text}
                                </p>
                                <div className="flex items-center gap-3 mt-1">
                                  {q.options?.map((opt, i) => (
                                    <span key={i} className={`font-body text-xs px-2 py-0.5 ${['A', 'B', 'C', 'D'][i] === q.correct ? 'bg-green-100 text-green-700 font-medium' : 'text-brown-light'}`}>
                                      {['A', 'B', 'C', 'D'][i]}. {opt}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openEditQuestion(qcm.id, qIdx, q)}
                                  className="p-1 text-brown-mid hover:text-terra transition-colors"
                                  title="Modifier"
                                  data-testid={`edit-q-${qIdx}`}
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => deleteQuestion(qcm.id, qIdx)}
                                  className="p-1 text-brown-mid hover:text-red-500 transition-colors"
                                  title="Supprimer"
                                  data-testid={`delete-q-${qIdx}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {adminQcms.length === 0 && (
                  <div className="bg-white border border-sand p-12 text-center">
                    <ClipboardList size={48} className="mx-auto text-sand mb-4" />
                    <p className="font-display text-xl text-brown">Aucun QCM</p>
                    <p className="font-body text-sm text-brown-mid">Les QCMs seront chargés automatiquement au démarrage.</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendrier">
            {(() => {
              const year = calendarMonth.getFullYear();
              const month = calendarMonth.getMonth();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const startPad = (firstDay.getDay() + 6) % 7;
              const daysInMonth = lastDay.getDate();
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const allSessions = allSessionsForCalendar;
              const getSessionsForDate = (day) => {
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                return allSessions.filter(s => s.date_jour1 === dateStr || s.date_jour2 === dateStr);
              };

              const statusColor = (s) => {
                if (s.status === 'closed' || s.status === 'cancelled') return 'bg-red-100 border-red-300 text-red-700';
                if (s.status === 'full') return 'bg-yellow-100 border-yellow-300 text-yellow-700';
                const d = new Date(s.date_jour2 + 'T23:59:59');
                if (d < today) return 'bg-gray-100 border-gray-300 text-gray-500';
                return 'bg-green-100 border-green-300 text-green-700';
              };

              const cells = [];
              for (let i = 0; i < startPad; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) cells.push(d);
              while (cells.length % 7 !== 0) cells.push(null);

              return (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <button onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-cream transition-colors" data-testid="cal-prev"><ChevronLeft size={20} /></button>
                    <h3 className="font-display text-xl text-brown capitalize">
                      {calendarMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </h3>
                    <button onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-cream transition-colors" data-testid="cal-next"><ChevronRight size={20} /></button>
                  </div>

                  <div className="bg-white border border-sand" data-testid="calendar-grid">
                    <div className="grid grid-cols-7 border-b border-sand">
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                        <div key={d} className="p-2 text-center form-label">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7">
                      {cells.map((day, i) => {
                        const sessions = day ? getSessionsForDate(day) : [];
                        const isToday = day && year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
                        return (
                          <div key={i} className={`min-h-[100px] p-2 border-b border-r border-sand/50 ${!day ? 'bg-cream/50' : ''} ${isToday ? 'bg-terra/5' : ''}`}>
                            {day && (
                              <>
                                <span className={`font-body text-sm ${isToday ? 'font-bold text-terra' : 'text-brown-mid'}`}>{day}</span>
                                <div className="mt-1 space-y-1">
                                  {sessions.map(s => (
                                    <button
                                      key={s.id}
                                      onClick={() => fetchSessionDetail(s.id)}
                                      className={`w-full text-left px-2 py-1 border text-[10px] font-body leading-tight cursor-pointer hover:opacity-80 transition-opacity ${statusColor(s)}`}
                                      data-testid={`cal-session-${s.id}`}
                                    >
                                      <span className="font-medium">{s.date_jour1 === `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` ? 'J1' : 'J2'}</span> {s.heure_debut}-{s.heure_fin}
                                      <br />{s.places_prises}/{s.places_total} · {s.lieu}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-6 font-body text-xs text-brown-mid">
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-green-100 border border-green-300" /> Ouvert</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-yellow-100 border border-yellow-300" /> Complet</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-100 border border-red-300" /> Fermé</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-gray-100 border border-gray-300" /> Passé</span>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          {/* Paramètres Tab */}
          <TabsContent value="emails">
            <div className="space-y-6" data-testid="emails-tab-content">
              {/* Manual send */}
              <div className="bg-white border border-sand p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Send size={20} className="text-terra" />
                  <h3 className="font-display text-xl text-brown">Envoyer un email</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="form-label block mb-2">DESTINATAIRES</label>
                    <select
                      className="form-input w-full"
                      value={manualEmail.target_type}
                      onChange={(e) => setManualEmail({ ...manualEmail, target_type: e.target.value, participant_id: '', session_id: '' })}
                      data-testid="manual-target-type"
                    >
                      <option value="participant">Un participant</option>
                      <option value="session">Tous les participants d&apos;une session</option>
                      <option value="all">Tous les participants</option>
                    </select>
                  </div>
                  {manualEmail.target_type === 'participant' && (
                    <div>
                      <label className="form-label block mb-2">PARTICIPANT</label>
                      <select
                        className="form-input w-full"
                        value={manualEmail.participant_id}
                        onChange={(e) => setManualEmail({ ...manualEmail, participant_id: e.target.value })}
                        data-testid="manual-participant-select"
                      >
                        <option value="">Choisir...</option>
                        {participants.map(p => (
                          <option key={p.id} value={p.id}>{p.prenom} · {p.email}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {manualEmail.target_type === 'session' && (
                    <div>
                      <label className="form-label block mb-2">SESSION</label>
                      <select
                        className="form-input w-full"
                        value={manualEmail.session_id}
                        onChange={(e) => setManualEmail({ ...manualEmail, session_id: e.target.value })}
                        data-testid="manual-session-select"
                      >
                        <option value="">Choisir...</option>
                        {allSessionsForCalendar.map(s => (
                          <option key={s.id} value={s.id}>
                            {new Date(s.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} · {s.lieu}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="form-label block mb-2">PRÉ-REMPLIR AVEC UN MODÈLE</label>
                    <select
                      className="form-input w-full"
                      value={manualTemplateKey}
                      onChange={(e) => loadTemplateIntoManual(e.target.value)}
                      data-testid="manual-template-select"
                    >
                      <option value="">— Aucun —</option>
                      {emailTemplates.map(t => (
                        <option key={t.key} value={t.key}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label block mb-2">OBJET</label>
                  <input
                    type="text"
                    className="form-input w-full"
                    value={manualEmail.subject}
                    onChange={(e) => setManualEmail({ ...manualEmail, subject: e.target.value })}
                    placeholder="Objet de l'email"
                    data-testid="manual-email-subject"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label block mb-2">MESSAGE</label>
                  <textarea
                    className="form-input w-full min-h-[160px]"
                    value={manualEmail.body}
                    onChange={(e) => setManualEmail({ ...manualEmail, body: e.target.value })}
                    placeholder="Votre message..."
                    data-testid="manual-email-body"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['{prenom}', '{email}', '{date_formation}', '{format_formation}', '{montant}', '{lien_espace}'].map(v => (
                    <span key={v} className="px-2 py-1 text-xs font-body bg-cream-dark text-brown-mid border border-sand">{v}</span>
                  ))}
                </div>
                <button
                  onClick={sendManualEmail}
                  disabled={sendingManual}
                  className="cta-button flex items-center gap-2 disabled:opacity-50"
                  data-testid="manual-send-button"
                >
                  <Send size={16} />
                  {sendingManual ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>

              {/* Templates */}
              <div className="bg-white border border-sand p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText size={20} className="text-terra" />
                  <h3 className="font-display text-xl text-brown">Modèles d&apos;emails</h3>
                </div>
                <p className="font-body text-sm text-brown-mid mb-5">
                  Personnalisez l&apos;objet et le contenu. Utilisez les variables (ex : <code className="text-terra">{'{prenom}'}</code>) — elles seront remplacées automatiquement. Activez/désactivez l&apos;envoi automatique avec l&apos;interrupteur.
                </p>
                <div className="space-y-4">
                  {emailTemplates.map((t) => {
                    const draft = templateDrafts[t.key] || { subject: t.subject, body: t.body, enabled: t.enabled };
                    return (
                      <div key={t.key} className="border border-sand" data-testid={`template-${t.key}`}>
                        <div className="flex items-center justify-between bg-cream-dark px-4 py-3">
                          <div className="flex items-center gap-3">
                            <h4 className="font-display text-base text-brown">{t.name}</h4>
                            <span className={`px-2 py-0.5 text-xs ${t.audience === 'admin' ? 'bg-brown/10 text-brown-mid' : 'bg-terra/10 text-terra'}`}>
                              {t.audience === 'admin' ? 'Admin' : 'Participant'}
                            </span>
                            {t.auto && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700">Auto</span>}
                          </div>
                          <button
                            onClick={() => toggleTemplate(t.key)}
                            className={`relative inline-flex items-center h-6 w-11 transition-colors ${draft.enabled ? 'bg-green-500' : 'bg-sand'}`}
                            title={draft.enabled ? 'Activé' : 'Désactivé'}
                            data-testid={`toggle-template-${t.key}`}
                          >
                            <span className={`inline-block h-4 w-4 bg-white transform transition-transform ${draft.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <label className="form-label block mb-1">OBJET</label>
                            <input
                              type="text"
                              className="form-input w-full"
                              value={draft.subject}
                              onChange={(e) => updateDraft(t.key, 'subject', e.target.value)}
                              data-testid={`template-subject-${t.key}`}
                            />
                          </div>
                          <div>
                            <label className="form-label block mb-1">CONTENU</label>
                            <textarea
                              className="form-input w-full min-h-[140px] font-body text-sm"
                              value={draft.body}
                              onChange={(e) => updateDraft(t.key, 'body', e.target.value)}
                              data-testid={`template-body-${t.key}`}
                            />
                          </div>
                          <button
                            onClick={() => saveTemplate(t.key)}
                            disabled={savingTemplate === t.key}
                            className="flex items-center gap-2 px-4 py-2 border border-terra text-terra hover:bg-terra hover:text-white transition-colors disabled:opacity-50"
                            data-testid={`save-template-${t.key}`}
                          >
                            <Save size={14} />
                            {savingTemplate === t.key ? 'Enregistrement...' : 'Enregistrer'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* History */}
              <div className="bg-white border border-sand p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={20} className="text-terra" />
                  <h3 className="font-display text-xl text-brown">Historique des envois</h3>
                </div>
                {emailLogs.length === 0 ? (
                  <p className="font-body text-sm text-brown-mid">Aucun email envoyé pour le moment.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="email-logs-table">
                      <thead className="bg-cream-dark border-b border-sand">
                        <tr>
                          <th className="form-label px-4 py-2 text-left">DATE</th>
                          <th className="form-label px-4 py-2 text-left">DESTINATAIRE</th>
                          <th className="form-label px-4 py-2 text-left">OBJET</th>
                          <th className="form-label px-4 py-2 text-center">STATUT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log) => (
                          <tr key={log.id} className="border-b border-sand/50">
                            <td className="px-4 py-2 font-body text-xs text-brown-mid whitespace-nowrap">
                              {new Date(log.created_at).toLocaleDateString('fr-FR')} {new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-4 py-2 font-body text-xs text-brown">{log.to}</td>
                            <td className="px-4 py-2 font-body text-xs text-brown-mid">{log.subject}</td>
                            <td className="px-4 py-2 text-center">
                              {log.status === 'sent' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700"><CheckCircle size={12} /> Envoyé</span>
                              ) : log.status === 'skipped' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-500">Désactivé</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-100 text-red-700"><XCircle size={12} /> Échec</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="parametres">
            <div className="space-y-6">
              {/* Revolut API Key */}
              <div className="bg-white border border-sand p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Key size={20} className="text-terra" />
                  <h3 className="font-display text-xl text-brown">Revolut Business API</h3>
                </div>
                <p className="font-body text-sm text-brown-mid mb-4">
                  Configurez votre clé API Revolut Business pour automatiser le suivi des paiements.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="form-label block mb-2">STATUT</label>
                    <div className="flex items-center gap-2">
                      {adminSettings?.revolut_configured ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700">
                          <CheckCircle size={14} /> Configurée · {adminSettings.revolut_key_masked}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-yellow-100 text-yellow-700">
                          <AlertCircle size={14} /> Non configurée
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="form-label block mb-2">MODE</label>
                    <select
                      className="form-input w-full max-w-xs"
                      value={revolutMode}
                      onChange={(e) => setRevolutMode(e.target.value)}
                      data-testid="revolut-mode-select"
                    >
                      <option value="sandbox">Sandbox (test)</option>
                      <option value="production">Production</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label block mb-2">
                      {adminSettings?.revolut_configured ? 'REMPLACER LA CLÉ API' : 'CLÉ API REVOLUT'}
                    </label>
                    <input
                      type="password"
                      className="form-input w-full max-w-lg"
                      value={revolutKeyInput}
                      onChange={(e) => setRevolutKeyInput(e.target.value)}
                      placeholder="sk_live_... ou sk_sandbox_..."
                      data-testid="revolut-key-input"
                    />
                  </div>

                  <button
                    onClick={saveRevolutSettings}
                    disabled={savingSettings}
                    className="cta-button flex items-center gap-2"
                    data-testid="save-revolut-settings"
                  >
                    <Save size={16} />
                    {savingSettings ? 'Sauvegarde...' : 'Sauvegarder'}
                  </button>
                </div>
              </div>

              {/* Email Config */}
              <div className="bg-white border border-sand p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Mail size={20} className="text-terra" />
                  <h3 className="font-display text-xl text-brown">Emails (Resend)</h3>
                </div>
                <p className="font-body text-sm text-brown-mid mb-4">
                  Les emails transactionnels sont envoyés via Resend. Gérez les modèles, l&apos;envoi manuel et l&apos;historique dans l&apos;onglet « Emails ».
                </p>
                <button
                  onClick={() => setActiveTab('emails')}
                  className="flex items-center gap-2 px-4 py-2 border border-sand hover:bg-cream transition-colors"
                  data-testid="goto-emails-tab"
                >
                  <Mail size={16} className="text-terra" />
                  <span className="font-body text-sm">Gérer les emails</span>
                </button>
              </div>

              {/* Session Settings */}
              <div className="bg-white border border-sand p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Settings size={20} className="text-terra" />
                  <h3 className="font-display text-xl text-brown">Session globale</h3>
                </div>
                <button
                  onClick={() => setShowSessionModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-sand hover:bg-cream transition-colors"
                  data-testid="open-session-settings"
                >
                  <Edit size={16} className="text-terra" />
                  <span className="font-body text-sm">Modifier les paramètres</span>
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Session Settings Modal */}
      <Dialog open={showSessionModal} onOpenChange={setShowSessionModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-lg" data-testid="session-modal">
          <DialogHeader className="px-6 py-4 border-b border-sand">
            <DialogTitle className="font-display text-xl text-brown">Paramètres de session</DialogTitle>
            <DialogDescription className="sr-only">Modifier les paramètres de la session</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label block mb-2">PLACES TOTALES</label>
                <input
                  type="number"
                  className="form-input w-full"
                  value={sessionForm.places_total || ''}
                  onChange={(e) => setSessionForm({ ...sessionForm, places_total: parseInt(e.target.value) })}
                  data-testid="input-places-total"
                />
              </div>
              <div>
                <label className="form-label block mb-2">PLACES PRISES</label>
                <input
                  type="number"
                  className="form-input w-full"
                  value={sessionForm.places_prises || ''}
                  onChange={(e) => setSessionForm({ ...sessionForm, places_prises: parseInt(e.target.value) })}
                  data-testid="input-places-prises"
                />
              </div>
            </div>
            <div>
              <label className="form-label block mb-2">DATE DE SESSION</label>
              <input
                type="text"
                className="form-input w-full"
                value={sessionForm.date_session || ''}
                onChange={(e) => setSessionForm({ ...sessionForm, date_session: e.target.value })}
                placeholder="Ex: 15 janvier 2025"
                data-testid="input-date-session"
              />
            </div>
            <div>
              <label className="form-label block mb-2">FORMAT</label>
              <input
                type="text"
                className="form-input w-full"
                value={sessionForm.format_session || ''}
                onChange={(e) => setSessionForm({ ...sessionForm, format_session: e.target.value })}
                data-testid="input-format-session"
              />
            </div>
            <div>
              <label className="form-label block mb-2">TARIF (€)</label>
              <input
                type="number"
                className="form-input w-full"
                value={sessionForm.tarif || ''}
                onChange={(e) => setSessionForm({ ...sessionForm, tarif: parseInt(e.target.value) })}
                data-testid="input-tarif"
              />
            </div>
            <div>
              <label className="form-label block mb-2">LIEN REVOLUT</label>
              <input
                type="text"
                className="form-input w-full"
                value={sessionForm.revolut_link || ''}
                onChange={(e) => setSessionForm({ ...sessionForm, revolut_link: e.target.value })}
                placeholder="https://pay.revolut.com/..."
                data-testid="input-revolut-link"
              />
            </div>
            <button
              onClick={handleUpdateSession}
              className="cta-button w-full flex items-center justify-center gap-2"
              data-testid="save-session-button"
            >
              <Save size={16} />
              Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Questionnaire Edit Modal */}
      <Dialog open={showQuestionnaireModal} onOpenChange={setShowQuestionnaireModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="questionnaire-modal">
          <DialogHeader className="px-6 py-4 border-b border-sand sticky top-0 bg-white z-10">
            <DialogTitle className="font-display text-xl text-brown">
              {selectedQuestionnaire ? 'Modifier le questionnaire' : 'Nouveau questionnaire'}
            </DialogTitle>
            <DialogDescription className="sr-only">Édition du questionnaire</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <div>
              <label className="form-label block mb-2">TITRE</label>
              <input
                type="text"
                className="form-input w-full"
                value={questionnaireForm.title}
                onChange={(e) => setQuestionnaireForm({ ...questionnaireForm, title: e.target.value })}
                placeholder="Ex: Questionnaire de mi-parcours"
                data-testid="input-questionnaire-title"
              />
            </div>
            <div>
              <label className="form-label block mb-2">TYPE</label>
              <select
                className="form-input w-full"
                value={questionnaireForm.type}
                onChange={(e) => setQuestionnaireForm({ ...questionnaireForm, type: e.target.value })}
                data-testid="select-questionnaire-type"
              >
                <option value="milieu">Mi-parcours</option>
                <option value="fin">Fin de formation</option>
              </select>
            </div>

            <div className="border-t border-sand pt-4">
              <div className="flex items-center justify-between mb-4">
                <label className="form-label">QUESTIONS</label>
                <button
                  onClick={addQuestion}
                  className="text-terra text-sm font-body flex items-center gap-1 hover:underline"
                  data-testid="add-question-button"
                >
                  <Plus size={14} />
                  Ajouter
                </button>
              </div>

              <div className="space-y-4">
                {questionnaireForm.questions.map((question, index) => (
                  <div key={index} className="bg-cream p-4 border border-sand" data-testid={`question-${index}`}>
                    <div className="flex items-start gap-4">
                      <span className="font-display text-lg text-terra font-semibold">{index + 1}</span>
                      <div className="flex-1 space-y-3">
                        <input
                          type="text"
                          className="form-input w-full"
                          value={question.text}
                          onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                          placeholder="Texte de la question"
                          data-testid={`question-text-${index}`}
                        />
                        <select
                          className="form-input w-full"
                          value={question.type}
                          onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                          data-testid={`question-type-${index}`}
                        >
                          <option value="text">Texte libre</option>
                          <option value="choice">Choix multiple</option>
                          <option value="rating">Note (1-5)</option>
                        </select>
                        {question.type === 'choice' && (
                          <input
                            type="text"
                            className="form-input w-full"
                            value={(question.options || []).join(', ')}
                            onChange={(e) => updateQuestion(index, 'options', e.target.value.split(',').map(s => s.trim()))}
                            placeholder="Options séparées par des virgules"
                            data-testid={`question-options-${index}`}
                          />
                        )}
                      </div>
                      <button
                        onClick={() => removeQuestion(index)}
                        className="p-1 hover:bg-white transition-colors"
                        data-testid={`remove-question-${index}`}
                      >
                        <X size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleSaveQuestionnaire}
              className="cta-button w-full flex items-center justify-center gap-2 mt-6"
              data-testid="save-questionnaire-button"
            >
              <Save size={16} />
              {selectedQuestionnaire ? 'Mettre à jour' : 'Créer le questionnaire'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal (from main view) */}
      <Dialog open={showPaymentModal && !showParticipantDetail} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-md">
          <DialogHeader className="px-6 py-4 border-b border-sand">
            <DialogTitle className="font-display text-xl text-brown">
              Enregistrer un paiement {selectedParticipant && `- ${selectedParticipant.prenom}`}
            </DialogTitle>
            <DialogDescription className="sr-only">Formulaire de paiement</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <div>
              <label className="form-label block mb-2">MONTANT (€)</label>
              <input
                type="number"
                className="form-input w-full"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <label className="form-label block mb-2">RÉFÉRENCE (optionnel)</label>
              <input
                type="text"
                className="form-input w-full"
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                placeholder="Ex: Revolut #123456"
              />
            </div>
            <button
              onClick={handleMarkPayment}
              className="cta-button w-full flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} />
              Confirmer le paiement
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Training Session Modal */}
      <Dialog open={showCreateSessionModal} onOpenChange={setShowCreateSessionModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-lg" data-testid="create-session-modal">
          <DialogHeader className="px-6 py-4 border-b border-sand">
            <DialogTitle className="font-display text-xl text-brown">Nouveau créneau de formation</DialogTitle>
            <DialogDescription className="sr-only">Créer un nouveau créneau de formation</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label block mb-2">JOUR 1 (LUNDI)</label>
                <input
                  type="date"
                  className="form-input w-full"
                  value={newSessionForm.date_jour1}
                  onChange={(e) => autoFillJour2(e.target.value)}
                  data-testid="input-date-jour1"
                />
              </div>
              <div>
                <label className="form-label block mb-2">JOUR 2 (MARDI)</label>
                <input
                  type="date"
                  className="form-input w-full"
                  value={newSessionForm.date_jour2}
                  onChange={(e) => setNewSessionForm({ ...newSessionForm, date_jour2: e.target.value })}
                  data-testid="input-date-jour2"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label block mb-2">HEURE DÉBUT</label>
                <input
                  type="time"
                  className="form-input w-full"
                  value={newSessionForm.heure_debut}
                  onChange={(e) => setNewSessionForm({ ...newSessionForm, heure_debut: e.target.value })}
                  data-testid="input-heure-debut"
                />
              </div>
              <div>
                <label className="form-label block mb-2">HEURE FIN</label>
                <input
                  type="time"
                  className="form-input w-full"
                  value={newSessionForm.heure_fin}
                  onChange={(e) => setNewSessionForm({ ...newSessionForm, heure_fin: e.target.value })}
                  data-testid="input-heure-fin"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label block mb-2">PLACES</label>
                <input
                  type="number"
                  className="form-input w-full"
                  value={newSessionForm.places_total}
                  onChange={(e) => setNewSessionForm({ ...newSessionForm, places_total: parseInt(e.target.value) || 8 })}
                  data-testid="input-session-places"
                />
              </div>
              <div>
                <label className="form-label block mb-2">TARIF (€)</label>
                <input
                  type="number"
                  className="form-input w-full"
                  value={newSessionForm.tarif}
                  onChange={(e) => setNewSessionForm({ ...newSessionForm, tarif: parseInt(e.target.value) || 150 })}
                  data-testid="input-session-tarif"
                />
              </div>
            </div>
            <div>
              <label className="form-label block mb-2">LIEU</label>
              <input
                type="text"
                className="form-input w-full"
                value={newSessionForm.lieu}
                onChange={(e) => setNewSessionForm({ ...newSessionForm, lieu: e.target.value })}
                placeholder="Ex: Zoom, Paris, Lyon..."
                data-testid="input-session-lieu"
              />
            </div>
            <div>
              <label className="form-label block mb-2">DESCRIPTION (optionnel)</label>
              <textarea
                className="form-input w-full min-h-[80px]"
                value={newSessionForm.description}
                onChange={(e) => setNewSessionForm({ ...newSessionForm, description: e.target.value })}
                placeholder="Ex: Session spéciale débutants..."
                data-testid="input-session-description"
              />
            </div>
            <button
              onClick={handleCreateTrainingSession}
              className="cta-button w-full flex items-center justify-center gap-2"
              data-testid="save-session-button"
            >
              <Plus size={16} />
              Créer le créneau
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QCM & Questionnaire Detail Modal */}
      <Dialog open={showQcmDetailModal} onOpenChange={setShowQcmDetailModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="qcm-detail-modal">
          <DialogHeader className="px-6 py-4 border-b border-sand sticky top-0 bg-white z-10">
            <DialogTitle className="font-display text-xl text-brown">
              Réponses de {qcmDetailData?.participantName}
            </DialogTitle>
            <DialogDescription className="sr-only">Détails des QCMs et questionnaires</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-6">
            {qcmDetailData?.qcms?.length > 0 && qcmDetailData.qcms.map((qcm) => (
              <div key={qcm.qcm_id} className="border border-sand">
                <div className="bg-cream-dark px-4 py-3 flex items-center justify-between">
                  <h4 className="font-display text-lg text-brown">QCM Jour {qcm.qcm_day}</h4>
                  <span className={`font-display text-lg font-semibold ${qcm.score_percent >= 70 ? 'text-green-600' : 'text-red-500'}`}>
                    {qcm.score}/{qcm.total} ({qcm.score_percent}%)
                  </span>
                </div>
                <div className="divide-y divide-sand/50">
                  {qcm.questions?.map((q, i) => (
                    <div key={i} className={`px-4 py-3 ${q.is_correct ? 'bg-green-50/50' : 'bg-red-50/50'}`}>
                      <div className="flex items-start gap-3">
                        <span className={`mt-0.5 flex-shrink-0 ${q.is_correct ? 'text-green-600' : 'text-red-500'}`}>
                          {q.is_correct ? <CheckCircle size={16} /> : <XCircle size={16} />}
                        </span>
                        <div className="flex-1">
                          <p className="font-body text-sm text-brown mb-1">
                            <strong>Q{i + 1}.</strong> {q.text}
                          </p>
                          <div className="flex items-center gap-4 font-body text-xs">
                            <span className={q.is_correct ? 'text-green-700' : 'text-red-600'}>
                              Réponse : {q.user_answer || '—'}
                            </span>
                            {!q.is_correct && (
                              <span className="text-green-700">
                                Correcte : {q.correct_answer}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {qcmDetailData?.questionnaires?.length > 0 && (
              <>
                <h4 className="font-display text-lg text-brown mt-6">Questionnaires</h4>
                {qcmDetailData.questionnaires.map((quest) => (
                  <div key={quest.questionnaire_id} className="border border-sand">
                    <div className="bg-cream-dark px-4 py-3">
                      <h5 className="font-display text-base text-brown">{quest.questionnaire_title}</h5>
                      <span className="font-body text-xs text-brown-light">
                        {quest.questionnaire_type === 'milieu' ? 'Mi-parcours' : 'Fin de formation'} · {quest.submitted_at ? new Date(quest.submitted_at).toLocaleDateString('fr-FR') : ''}
                      </span>
                    </div>
                    <div className="divide-y divide-sand/50">
                      {quest.answers?.map((a, i) => (
                        <div key={i} className="px-4 py-3">
                          <p className="font-body text-sm font-medium text-brown mb-1">{a.question}</p>
                          <p className="font-body text-sm text-terra">{a.answer || '—'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}

            {(!qcmDetailData?.qcms?.length && !qcmDetailData?.questionnaires?.length) && (
              <p className="font-body text-sm text-brown-mid text-center py-8">Aucune réponse enregistrée pour ce participant</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
